import * as React from "react";

import {
  concatStyleSets,
  DirectionalHint,
  IKeytipProps,
  Stack,
  VerticalDivider,
} from "@fluentui/react";

import {
  ActionControlSize,
  getHoverableTooltipsPreference,
} from "@1js/acui-common";
import { AppFontStylesTwoRowPreviewGalleryButton } from "@1js/acui-gallery-font-styles";
import { getPropsWithMenuDismiss } from "@1js/acui-menu";

import { ControlRendererFunction } from "../../../controls";
import {
  RibbonControlInternalProps,
  RibbonControlInternalUnionProps,
  RibbonControlProps,
} from "../../Controls/RibbonControlProps";
import { getRibbonGroupItemsIcon } from "../RibbonControlGroup/getRibbonGroupItemsIcon";
import { LayoutChanges } from "../RibbonControlGroup/RibbonControlGroupAnimation";
import { RibbonEnlightenedComponent } from "../RibbonEnlightenedComponent";
import {
  deriveMLRBottomRowKeytipProps,
  deriveMLRMiddleRowKeytipProps,
  deriveMLRTopRowKeytipProps,
} from "./deriveMLRKeytipProps";
import { getRibbonControlGroupItemsProps } from "./getRibbonControlGroupItemsProps";
import { getRibbonControlGroupItemsStyleProps } from "./getRibbonControlGroupItemsStyles";
import {
  getRowDividerClassNames,
  getRowStackTokens,
  getSectionDividerClassNames,
} from "./layoutRenderer.classNames";
import {
  ControlId,
  Layout,
  MLRKeytipLocation,
  MultiRowSectionItem,
  Section,
  sections,
  singleRow,
  SingleRowSectionItem,
} from "./layoutTypings";

export type WithKey<T> = T & { key?: string };
type IdToPropsMap = {
  [controlId: string]: WithKey<RibbonControlInternalUnionProps>;
};

export function getDefaultLayout(
  controlProps: RibbonControlInternalUnionProps[]
) {
  return sections(singleRow(...controlProps.map((c) => c.id)));
}

export interface LayoutRendererProps {
  layout?: Layout;
  controlProps: RibbonControlInternalUnionProps[];
  controlRendererFunction: ControlRendererFunction;
  animate: boolean;
  layoutChanges?: LayoutChanges;
  dismissMenu?: () => void;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
  shouldAlignPreviewGalleryVertically?: boolean;
}

export const LayoutRenderer: React.FunctionComponent<LayoutRendererProps> = (
  props: LayoutRendererProps
) => {
  const {
    layout,
    controlProps,
    controlRendererFunction,
    animate,
    layoutChanges,
    dismissMenu,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
    shouldAlignPreviewGalleryVertically,
  } = props;
  const layoutToRender = layout || getDefaultLayout(controlProps);

  const controls = createPropsMap(controlProps);

  // We want to be able to center a control if the group text is longer than the width of the button
  // We will only center if we have a SingleRowSection that doesn't contain any stacked controls.
  const centerControls =
    layoutToRender.sections.length === 1 && // Is there only one section in the group
    Array.isArray(layoutToRender.sections[0]) && // Is the item not a divider
    layoutToRender.sections[0].length === 1 && // Is there only one section of items in the group
    (layoutToRender.sections[0][0] as SingleRowSectionItem[]) !== null && // Are we not a multi-row section
    (layoutToRender.sections[0][0] as SingleRowSectionItem[]).filter((item) =>
      Array.isArray(item)
    ).length === 0; // Are there any stacked controls

  return (
    <>
      <Stack
        verticalFill={true}
        horizontal={true}
        style={centerControls ? { justifyContent: "center" } : undefined}
      >
        {layoutToRender.sections.map((section, index) =>
          Array.isArray(section) ? (
            <SectionRenderer
              key={`section${section}`}
              section={section}
              controls={controls}
              controlRendererFunction={controlRendererFunction}
              animate={animate}
              layoutChanges={layoutChanges}
              dismissMenu={dismissMenu}
              shouldAlignPreviewGalleryVertically={
                shouldAlignPreviewGalleryVertically
              }
              isRibbonSpanning={isRibbonSpanning}
              spanningComponentId={spanningComponentId}
              spanningOffset={spanningOffset}
            />
          ) : (
            <VerticalDivider
              key={`divider${index}`}
              getClassNames={getSectionDividerClassNames}
            />
          )
        )}
      </Stack>
    </>
  );
};

interface SectionRendererProps {
  section: Section;
  controls: IdToPropsMap;
  controlRendererFunction: ControlRendererFunction;
  animate: boolean;
  layoutChanges?: LayoutChanges;
  dismissMenu?: () => void;
  shouldAlignPreviewGalleryVertically?: boolean;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
}

const SectionRenderer: React.FunctionComponent<SectionRendererProps> = (
  props: SectionRendererProps
) => {
  const {
    section,
    controls,
    controlRendererFunction,
    animate,
    layoutChanges,
    dismissMenu,
    shouldAlignPreviewGalleryVertically,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
  } = props;
  const contents: JSX.Element[] = [];
  const rowCount = section.length;

  // If there's only one row, draw controls Large, if there's multi-row, there's not enough space to render large
  const controlSize =
    rowCount === 1 ? ActionControlSize.Large : ActionControlSize.Small;

  for (let i = 0; i < section.length; i += 1) {
    let keytipLocation = i;
    // The last section should be marked as Bottom
    if (i === section.length - 1) {
      keytipLocation = MLRKeytipLocation.Bottom;
    }
    contents.push(
      <RowRenderer
        row={section[i]}
        controls={controls}
        controlRendererFunction={controlRendererFunction}
        defaultControlSize={controlSize}
        key={`row${i}`}
        keytipLocation={keytipLocation}
        animate={animate}
        layoutChanges={layoutChanges}
        dismissMenu={dismissMenu}
        isConsciousControl={section.length === 1}
        isRibbonSpanning={isRibbonSpanning}
        spanningComponentId={spanningComponentId}
        spanningOffset={spanningOffset}
        shouldAlignPreviewGalleryVertically={
          shouldAlignPreviewGalleryVertically
        }
      />
    );
  }

  const stackComponent = (
    <Stack tokens={getRowStackTokens(rowCount)}>{contents}</Stack>
  );
  const sectionId = `section${section}`;
  return section.length > 1 && isRibbonSpanning ? (
    <RibbonEnlightenedComponent
      id={sectionId}
      offset={spanningComponentId === sectionId ? spanningOffset : 0}
    >
      {stackComponent}
    </RibbonEnlightenedComponent>
  ) : (
    stackComponent
  );
};

/**
 * Renders a spacer to add padding between a large control and a stack of small controls
 */
const Spacer = () => <div key="spacer" style={{ width: "4px" }} />;

interface RowRendererProps {
  row: SingleRowSectionItem[] | MultiRowSectionItem[];
  controls: IdToPropsMap;
  controlRendererFunction: ControlRendererFunction;
  defaultControlSize: ActionControlSize;
  key: string;
  keytipLocation: MLRKeytipLocation;
  animate: boolean;
  layoutChanges?: LayoutChanges;
  dismissMenu?: () => void;
  isConsciousControl?: boolean;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
  shouldAlignPreviewGalleryVertically?: boolean;
}

/**
 * Renders a single row whose elements may be:
 *  1. A control ID
 *  2. An array of control IDs to be stacked vertically
 *  3. A divider
 */
const RowRenderer: React.FunctionComponent<RowRendererProps> = (
  props: RowRendererProps
) => {
  const {
    row,
    controls,
    controlRendererFunction,
    defaultControlSize,
    key,
    keytipLocation,
    animate,
    layoutChanges,
    dismissMenu,
    isConsciousControl,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
    shouldAlignPreviewGalleryVertically,
  } = props;
  const rowItems: JSX.Element[] = [];
  let dividerCount = 0;
  let prevItem = undefined;

  for (const item of row) {
    if (typeof item === "string") {
      // If the item is not in controls to be rendered.  The control either doens't exist in the group or has been hidden.
      if (!controls[item]) {
        continue;
      }

      // If the previous item was a stack, add a spacer before this large control
      if (Array.isArray(prevItem)) {
        rowItems.push(<Spacer />);
      }

      // #1 Render the control at the given size
      const itemHasMovedInLayout = layoutChanges && layoutChanges.moving[item];
      if (itemHasMovedInLayout) {
        rowItems.push(
          <div key={item} className="moved">
            <ControlRenderer
              controlId={item}
              controls={controls}
              size={defaultControlSize}
              controlRendererFunction={controlRendererFunction}
              renderOptions={{
                animate,
                keytipLocation,
              }}
              shouldAlignPreviewGalleryVertically={
                shouldAlignPreviewGalleryVertically
              }
              dismissMenu={dismissMenu}
              isConsciousControl={isConsciousControl}
              isRibbonSpanning={isRibbonSpanning}
              spanningComponentId={spanningComponentId}
              spanningOffset={spanningOffset}
            />
          </div>
        );
      } else {
        rowItems.push(
          <ControlRenderer
            key={item}
            controlId={item}
            controls={controls}
            size={defaultControlSize}
            controlRendererFunction={controlRendererFunction}
            renderOptions={{
              animate,
              keytipLocation,
            }}
            shouldAlignPreviewGalleryVertically={
              shouldAlignPreviewGalleryVertically
            }
            dismissMenu={dismissMenu}
            isConsciousControl={isConsciousControl}
            isRibbonSpanning={isRibbonSpanning}
            spanningComponentId={spanningComponentId}
            spanningOffset={spanningOffset}
          />
        );
      }
    } else if (Array.isArray(item)) {
      // Filter out items that cannot be rendered because we don't have props
      const visibleItems = item.filter(
        (control) => control != null && controls[control]
      ) as string[];
      if (visibleItems.length === 0) {
        continue;
      }

      // If the previous item was a large control, add a spacer before this stack
      if (typeof prevItem === "string") {
        rowItems.push(<Spacer />);
      }

      // #2 Render the items as a vertical stack (row is a SingleRowSectionItem[] and item is a VerticalControlStack)
      const stackedItems: JSX.Element[] = [];
      let hasMovedControls = false;
      visibleItems.map((stackedControl, layoutRow) => {
        if (layoutChanges && layoutChanges.moving[stackedControl]) {
          hasMovedControls = true;
        }

        stackedItems.push(
          <ControlRenderer
            key={stackedControl}
            controlId={stackedControl}
            controls={controls}
            size={ActionControlSize.Small}
            controlRendererFunction={controlRendererFunction}
            renderOptions={{
              animate,
              keytipLocation: layoutRow,
              stackedItems: visibleItems,
            }}
            shouldAlignPreviewGalleryVertically={
              shouldAlignPreviewGalleryVertically
            }
            dismissMenu={dismissMenu}
          />
        );
      });

      const rowStackComponent = (
        <Stack
          id={item.ariaProps && item.ariaProps.id}
          aria-labelledby={item.ariaProps && item.ariaProps.labelledBy}
          role={
            item.ariaProps && item.ariaProps.labelledBy ? "group" : undefined
          }
          horizontalAlign="start"
          className={hasMovedControls ? "moved" : undefined}
          tokens={getRowStackTokens(item.length)}
          key={`controlStack-${JSON.stringify(item)} `}
        >
          {stackedItems}
        </Stack>
      );
      const stackId = `controlStack-${JSON.stringify(item)}`;
      rowItems.push(
        isConsciousControl && isRibbonSpanning ? (
          <RibbonEnlightenedComponent
            id={stackId}
            offset={spanningComponentId === stackId ? spanningOffset : 0}
          >
            {rowStackComponent}
          </RibbonEnlightenedComponent>
        ) : (
          rowStackComponent
        )
      );
    } else {
      // #3 Render a vertical bar
      dividerCount += 1;
      rowItems.push(
        <VerticalDivider
          key={`divider${dividerCount}`}
          getClassNames={getRowDividerClassNames}
        />
      );
    }

    prevItem = item;
  }

  return (
    <Stack key={key} horizontal={true}>
      {rowItems}
    </Stack>
  );
};

type RenderControlOptions = {
  keytipLocation: MLRKeytipLocation;
  animate: boolean;
  stackedItems?: string[];
};

function getTooltipPosition(
  extraUILocation: MLRKeytipLocation
): DirectionalHint | undefined {
  // If we are using hoverable tooltips, set tooltip location based on position in MLR
  if (getHoverableTooltipsPreference()) {
    return extraUILocation === MLRKeytipLocation.Top
      ? DirectionalHint.topCenter
      : DirectionalHint.bottomCenter;
  }
  return undefined;
}

interface ControlRendererProps {
  controlId: ControlId;
  controls: IdToPropsMap;
  size: ActionControlSize;
  controlRendererFunction: ControlRendererFunction;
  renderOptions: RenderControlOptions;
  shouldAlignPreviewGalleryVertically?: boolean;
  dismissMenu?: () => void;
  isConsciousControl?: boolean;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
}

/** Renders a control at a given size with a key */

const ControlRenderer: React.FunctionComponent<ControlRendererProps> = (
  props: ControlRendererProps
) => {
  const {
    controlId,
    controls,
    size,
    controlRendererFunction,
    renderOptions,
    shouldAlignPreviewGalleryVertically,
    dismissMenu,
    isConsciousControl,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
  } = props;
  const controlProps = controls[controlId];

  const propsToRender = {
    // This line works without a cast to "any" but brings with it a 4x longer compile time.
    // This compile time issue was exposed with a TS4.1 "complex union" error and should eventually be fixed.
    ...(getPropsWithMenuDismissInternal(
      controlProps as RibbonControlProps,
      dismissMenu
    ) as any),
    size,
    key: `${controlId}-${size}`,
    animate: renderOptions.animate,
    downChevronIconSize: "icon8",
    keytipProps: getAugmentedKeytipProps(
      controlProps,
      renderOptions.keytipLocation,
      !!renderOptions.stackedItems
    ),
    tooltipDirection:
      controlProps.tooltipDirection ||
      getTooltipPosition(renderOptions.keytipLocation),
    ...getPropsBasedOnSize(
      controlProps as RibbonControlProps,
      size,
      shouldAlignPreviewGalleryVertically
    ),
    ...getRibbonControlGroupItemsProps(
      controlProps as RibbonControlProps,
      size
    ),
  } as RibbonControlInternalProps;

  const control = controlRendererFunction({
    ...propsToRender,
    ...getLayoutProps(propsToRender, controls, renderOptions),
  } as RibbonControlInternalProps);

  return isConsciousControl && isRibbonSpanning ? (
    <RibbonEnlightenedComponent
      id={controlId}
      offset={spanningComponentId === controlId ? spanningOffset : 0}
    >
      {control}
    </RibbonEnlightenedComponent>
  ) : (
    control
  );
};

function createPropsMap(
  controls: WithKey<RibbonControlInternalUnionProps>[]
): IdToPropsMap {
  const map: IdToPropsMap = {};

  for (const control of controls) {
    map[control.id] = control;
  }

  return map;
}

function getPropsBasedOnSize(
  props: RibbonControlProps,
  size: ActionControlSize,
  shouldAlignPreviewGalleryVertically?: boolean
) {
  return {
    icon: getRibbonGroupItemsIcon(props, size),
    ...getRibbonControlGroupItemsStyleProps(
      props,
      size,
      shouldAlignPreviewGalleryVertically
    ),
    getGalleryButtonComponent:
      props.type === "AppFontStylesPreviewGalleryProps" &&
      size === ActionControlSize.Large
        ? AppFontStylesTwoRowPreviewGalleryButton
        : undefined,
  };
}

function getLayoutProps(
  props: RibbonControlInternalProps,
  controls: IdToPropsMap,
  renderOptions: RenderControlOptions
) {
  if (props.type === "AppComboBoxProps" && renderOptions.stackedItems) {
    const allStackedItemsAreComboBoxes = renderOptions.stackedItems.every(
      (item) => controls[item] && controls[item].type === "AppComboBoxProps"
    );
    if (allStackedItemsAreComboBoxes) {
      // When a stack consists of only combo boxes, align their control boxes
      return {
        styles: concatStyleSets(props.styles, {
          controlContainer: {
            width: "100%",
            flex: 1,
          },
          controlAndIconContainer: {
            width: "100%",
            flex: 1,
          },
          container: { width: "100%" },
          label: { flex: 1, width: "100%" },
        }),
      };
    }
    // If a combo box is in a stack and has other controls, fill the container
    return {
      styles: concatStyleSets(props.styles, {
        container: { width: "100%" },
      }),
    };
  }

  return {};
}

function getAugmentedKeytipProps(
  controlProps: RibbonControlInternalUnionProps,
  keytipLocation: MLRKeytipLocation,
  isVerticalStackElement?: boolean
): IKeytipProps | undefined {
  switch (controlProps.type) {
    case "AppButtonProps":
    case "AppInkButtonProps":
    case "AppToggleButtonProps":
    case "AppFlyoutAnchorProps":
    case "AppInkFlyoutAnchorProps":
    case "AppSplitButtonProps":
    case "AppComboBoxProps":
    case "AppSpinButtonProps":
    case "AppCheckboxProps":
    case "RibbonTableStylesPreviewGalleryProps":
    case "AppFontStylesPreviewGalleryProps":
    case "AppIconPreviewGalleryProps":
    case "AppFontPickerProps":
    case "RibbonChangeColorsPreviewGalleryProps": {
      if (controlProps.keytipProps) {
        switch (keytipLocation) {
          case MLRKeytipLocation.Top:
            return deriveMLRTopRowKeytipProps(
              controlProps.keytipProps,
              isVerticalStackElement
            );
          case MLRKeytipLocation.Middle:
            return deriveMLRMiddleRowKeytipProps(
              controlProps.keytipProps,
              isVerticalStackElement
            );
          case MLRKeytipLocation.Bottom:
            return deriveMLRBottomRowKeytipProps(
              controlProps.keytipProps,
              isVerticalStackElement
            );
        }
      }
    }
  }
  return undefined;
}

function getPropsWithMenuDismissInternal(
  props: RibbonControlProps,
  dismissMenu?: () => void
) {
  switch (props.type) {
    case "AppButtonProps":
    case "AppToggleButtonProps":
    case "AppSplitButtonProps":
    case "AppCheckboxProps": {
      return dismissMenu ? getPropsWithMenuDismiss(props, dismissMenu) : props;
    }
  }
  return props;
}
